// distancing-cowork 의 /task/guides endpoint wrapper.
// REACT_APP_AGENT_API_HOST 미설정 시 in-memory mock (개발 fallback) — taskWorkingApi 와 동일 패턴.
//
// 가이드(tone/structure)는 백엔드 디스크 기본값(source="default") 위에 override(source="override")를
// 얹는 구조다. agent 는 read_file 로 가이드를 참고하며, 저장 즉시 다음 작성/검토 턴부터 반영된다.
//
// 동시 편집은 working_task 와 동일한 `version` optimistic lock — PUT 시 expected_version mismatch 면
// 409 + 현재 override 반환. 호출자는 GuideVersionConflictError 를 catch 해 최신본을 따라간다.
// (working_task 와 마찬가지로 본 endpoint 는 인증 미도입 — task editor 접근 권한은 FE 가 게이팅.)

const HOST = process.env.REACT_APP_AGENT_API_HOST || "";

export type GuideName = "tone" | "structure";

export interface GuideDTO {
  name: GuideName;
  path: string;
  content: string;
  version: number; // 0 = override 없음(디스크 기본값)
  source: "default" | "override";
  updated_by: string | null;
  updated_at: string | null;
}

export class GuideVersionConflictError extends Error {
  /** 409 응답 본문 — store 의 현재 (다른 editor 가 저장한) override. */
  current: GuideDTO;
  constructor(current: GuideDTO) {
    super(
      `guide version mismatch (name=${current.name}, current version=${current.version})`
    );
    this.name = "GuideVersionConflictError";
    this.current = current;
  }
}

const GUIDE_NAMES: GuideName[] = ["tone", "structure"];

// =========================
// In-memory mock (HOST 미설정 시)
// =========================

const MOCK_PATHS: Record<GuideName, string> = {
  tone: "/guides/02-tone.md",
  structure: "/guides/03-structure.md",
};

// HOST 없으면 실제 디스크 기본값을 못 읽으므로 placeholder 를 보여준다.
const MOCK_DEFAULTS: Record<GuideName, string> = {
  tone: "# 톤 가이드 (mock 기본값)\n\nREACT_APP_AGENT_API_HOST 가 설정되면 실제 백엔드 기본값을 불러옵니다.",
  structure:
    "# 구조 가이드 (mock 기본값)\n\nREACT_APP_AGENT_API_HOST 가 설정되면 실제 백엔드 기본값을 불러옵니다.",
};

const mockOverrides = new Map<GuideName, GuideDTO>();

function mockNow(): string {
  return new Date().toISOString();
}

function mockEffective(name: GuideName): GuideDTO {
  const override = mockOverrides.get(name);
  if (override) return override;
  return {
    name,
    path: MOCK_PATHS[name],
    content: MOCK_DEFAULTS[name],
    version: 0,
    source: "default",
    updated_by: null,
    updated_at: null,
  };
}

// =========================
// Public API
// =========================

export async function listGuides(): Promise<GuideDTO[]> {
  if (!HOST) return GUIDE_NAMES.map(mockEffective);
  const res = await fetch(`${HOST}/task/guides`, { method: "GET" });
  if (!res.ok) {
    throw new Error(`listGuides failed: ${res.status}`);
  }
  return (await res.json()) as GuideDTO[];
}

export async function putGuide(
  name: GuideName,
  content: string,
  expectedVersion: number
): Promise<GuideDTO> {
  if (!HOST) {
    const currentVersion = mockOverrides.get(name)?.version ?? 0;
    if (currentVersion !== expectedVersion) {
      throw new GuideVersionConflictError(mockEffective(name));
    }
    const next: GuideDTO = {
      name,
      path: MOCK_PATHS[name],
      content,
      version: currentVersion + 1,
      source: "override",
      updated_by: null,
      updated_at: mockNow(),
    };
    mockOverrides.set(name, next);
    return next;
  }
  const res = await fetch(`${HOST}/task/guides/${name}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content, expected_version: expectedVersion }),
  });
  if (res.status === 409) {
    throw new GuideVersionConflictError((await res.json()) as GuideDTO);
  }
  if (!res.ok) {
    throw new Error(`putGuide failed: ${res.status}`);
  }
  return (await res.json()) as GuideDTO;
}

// override 삭제 = 디스크 기본값으로 되돌리기. idempotent.
export async function resetGuide(name: GuideName): Promise<GuideDTO> {
  if (!HOST) {
    mockOverrides.delete(name);
    return mockEffective(name);
  }
  const res = await fetch(`${HOST}/task/guides/${name}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(`resetGuide failed: ${res.status}`);
  }
  return (await res.json()) as GuideDTO;
}

// =========================
// 테스트용 — mock state 리셋
// =========================

export function __resetGuidesMockForTests(): void {
  mockOverrides.clear();
}
